/** @format */

/* eslint require-await: "warn" */
import {
    BraintreeError,
    Client,
    DataCollector,
    HostedFieldFieldOptions,
    HostedFields,
    HostedFieldsTokenizePayload,
    PayPal,
    ThreeDSecure,
    ThreeDSecureVerifyPayload,
    client,
    dataCollector,
    hostedFields,
    threeDSecure,
} from 'braintree-web';
import paypalCheckout from 'braintree-web/paypal-checkout';

import { centToDecimal } from '@toucan/helpers/currency';

import { CheckoutType } from '@toucan/client/core/checkout';
import { PayPalCheckoutEnv } from '@toucan/client/global/nextjs';
import { Address, CustomerDetails } from '@toucan/client/global/transaction';

import { toAsciiPrintable } from './helpers';

export enum ErrorCodes {
    PAYPAL_ERROR,
    PAYPAL_DECLINED,
    PAYPAL_CREATE_PAYMENT_FAILED,
    BRAINTREE_DECLINED,
    BRAINTREE_ERROR,
    BRAINTREE_THREEDS_VALIDATION_ERROR,
}

export enum Braintree3DSecureStatus {
    AUTHENTICATE_FRICTIONLESS_FAILED = 'authenticate_frictionless_failed',
    AUTHENTICATE_ATTEMPT_SUCCESSFUL = 'authenticate_attempt_successful',
    AUTHENTICATE_UNABLE_TO_AUTHENTICATE = 'authenticate_unable_to_authenticate',
    AUTHENTICATE_REJECTED = 'authenticate_rejected',
    AUTHENTICATE_UNAVAILABLE = 'authentication_unavailable',
    LOOKUP_ERROR = 'lookup_error',
    LOOKUP_FAILED_ACS_ERROR = 'lookup_failed_acs_error',
    LOOKUP_BYPASSED = 'lookup_bypassed',
    AUTHENTICATE_SUCCESSFUL = 'authenticate_successful',
    CHALLENGE_REQUIRED = 'challenge_required',
    AUTHENTICATE_ERROR = 'authenticate_error',
    DATA_ONLY_SUCCESSFUL = 'data_only_successful',
}

enum PayPalErrorTypes {
    INSTRUMENT_DECLINED = 'INSTRUMENT_DECLINED',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SubmitCardPaymentResponse {
    nonce: string;
    deviceData?: string;
    checkoutType: CheckoutType;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ErrorResponse {
    code: ErrorCodes;
    message?: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface TransactionInfo {
    customer: CustomerDetails;
    billingAddress: Address;
    deliveryAddress: Address;
    total: number;
    currency: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface BraintreeClientCallbacks {
    onCheckoutAttempted: (checkoutType: CheckoutType) => void;
    onPayPalComplete: (nonce: string, details: any) => void;
    onError: (arg: ErrorResponse, checkoutType: CheckoutType) => void;
    onIframeAdd: (iframe: HTMLIFrameElement, reject: () => void) => void;
    onIframeRemove: () => void;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface BraintreeClientProps {
    authorization: string;
    paypalContainer?: HTMLDivElement | null;
    paypalCreditContainer?: HTMLDivElement | null;
    paypalEnabled?: boolean;
    paypalCreditEnabled?: boolean;
    paypalStyles?: PaypalButtonStyles;
    locale: string;
    transactionInfo: TransactionInfo;
    callbacks: BraintreeClientCallbacks;
    env: PayPalCheckoutEnv;
}

export type PaypalButtonStyles = Record<string, any>;

type WindowWithPayPal = Window & { paypal?: any };

const defaultPaypalStyles = {
    label: 'pay',
    size: 'medium',
    shape: 'rect',
    color: 'blue',
    tagline: false,
};

export class BraintreeClient {
    private readonly env: PayPalCheckoutEnv;
    private readonly authorization: string;
    private readonly locale: string;
    private readonly transactionInfo: TransactionInfo;
    private readonly paypalAttemptLimit: number;
    private readonly paypalEnabled: boolean;
    private readonly paypalCreditEnabled: boolean;
    private readonly paypalContainer: HTMLDivElement | null | undefined;
    private readonly paypalCreditContainer: HTMLDivElement | null | undefined;
    private readonly paypalStyles: PaypalButtonStyles;

    private clientInstance: Client | undefined;
    private hostedFieldsInstance: HostedFields | undefined;
    private threeDSecureInstance: ThreeDSecure | undefined;
    private dataCollectorInstance: DataCollector | undefined;
    private paypalCheckoutInstance: any;
    private callbacks: BraintreeClientCallbacks;

    // @ts-ignore
    private nonce: string;

    private fieldStyles = {
        input: {
            color: 'var(--colors-text-secondary)',
            'font-family': 'var(--typography-font)',
            'font-size': '15px',
            'font-weight': 300,
        },
    };

    private paypalCreditStyles = {
        label: 'credit',
        size: 'medium',
        shape: 'rect',
        color: 'darkblue',
        tagline: false,
    };

    private paypalAttemptCount: number;

    public constructor({
        paypalEnabled = false,
        paypalCreditEnabled = false,
        paypalStyles = defaultPaypalStyles,
        ...props
    }: BraintreeClientProps) {
        this.env = props.env;
        this.authorization = props.authorization;
        this.paypalEnabled = paypalEnabled;
        this.paypalCreditEnabled = paypalCreditEnabled;
        this.paypalContainer = props.paypalContainer;
        this.paypalCreditContainer = props.paypalCreditContainer;
        this.locale = props.locale;
        this.transactionInfo = props.transactionInfo;
        this.paypalStyles = paypalStyles;

        this.paypalAttemptLimit = 3;
        this.paypalAttemptCount = 0;

        this.callbacks = props.callbacks;
    }

    /**
     * Handle initializing the various parts of Braintree's SDK depending on
     * the required configuration. ThreeDSecure and DataCollector are always
     * setup and PayPal is optional
     *
     * @param [callback] An optional callback on completion
     */
    public async init(callback?: () => void) {
        try {
            this.clientInstance = await client.create({
                authorization: this.authorization,
            });
        } catch (error) {
            console.error('Error initializing Braintree Client');
            console.error(error);
        }

        if (!this.clientInstance) {
            throw new Error('No Braintree client instance found');
        }

        try {
            this.threeDSecureInstance = await threeDSecure.create({
                version: 2,
                client: this.clientInstance,
            });
        } catch (error) {
            console.error('Error initializing Braintree ThreeDSecure');
            console.error(error);
        }

        try {
            this.dataCollectorInstance = await dataCollector.create({
                client: this.clientInstance,
                kount: false, // TODO: Is this required? Seems to not be enabled on our account
                paypal: true,
            });
        } catch (error) {
            console.error('Error initializing Braintree Data Collector');
            console.error(error);
        }

        try {
            this.paypalCheckoutInstance =
                this.paypalEnabled &&
                (await new Promise((resolve, reject) => {
                    paypalCheckout.create(
                        {
                            client: this.clientInstance,
                        },
                        (error: BraintreeError, instance: any) => {
                            if (error) {
                                reject(error);
                            } else {
                                resolve(instance);
                            }
                        }
                    );
                }));
        } catch (error) {
            console.error('Error initializing PayPal Checkout');
            console.error(error);
        }

        callback && callback();
    }

    public async attachPayPalButton() {
        const { paypal } = window as WindowWithPayPal;

        if (!this.paypalEnabled) {
            return;
        }

        await paypal.Button.render(
            {
                env: this.env,
                locale: this.locale,
                style: this.paypalStyles,
                commit: true,
                payment: () => {
                    return this.handlePaypalPayment();
                },
                onAuthorize: async (data: any, actions: any) => {
                    return this.handlePaypalAuthorization(data, actions);
                },
                onError: (error: any) => {
                    console.error({
                        code: ErrorCodes.PAYPAL_ERROR,
                        message: error,
                    });
                },
            },
            this.paypalContainer
        );
    }

    public async attachPayPalCredit() {
        const { paypal } = window as WindowWithPayPal;

        if (!this.paypalCreditEnabled) {
            return;
        }

        await paypal.Button.render(
            {
                env: this.env,
                locale: this.locale,
                style: this.paypalCreditStyles,
                payment: () => {
                    return this.handlePaypalPayment();
                },
                onAuthorize: async (data: any, actions: any) => {
                    return this.handlePaypalAuthorization(data, actions);
                },
                onError: (error: any) => {
                    console.error({
                        code: ErrorCodes.PAYPAL_ERROR,
                        message: error,
                    });
                },
            },
            this.paypalCreditContainer
        );
    }

    /**
     * Handle attaching UI elements. This will bind the card hosted fields and
     * if PayPal and PayPal credit are enabled, the smart buttons to launch
     * the PayPal checkout flow.
     *
     * @param fields Fields to attach
     * @param [callback] An optional callback
     */
    public async attachElements(
        fields: HostedFieldFieldOptions,
        callback?: (arg: HostedFields, arg2: PayPal) => void
    ) {
        if (!this.clientInstance) {
            throw new Error('No Braintree client instance found');
        }

        this.hostedFieldsInstance = await hostedFields.create({
            client: this.clientInstance,
            fields,
            styles: this.fieldStyles,
        });

        await this.attachPayPalButton();
        await this.attachPayPalCredit();

        callback?.(this.hostedFieldsInstance, this.paypalCheckoutInstance);
    }

    /**
     * Handle submitting a card payment using Braintree's HostedFields SDK
     */
    public async submitCardPayment(): Promise<SubmitCardPaymentResponse> {
        this.callbacks.onCheckoutAttempted(CheckoutType.CARD);

        const [billingGivenName, billingSurname] =
            BraintreeClient.transformAddressee(
                this.transactionInfo.billingAddress.addressee
            );
        const [shippingGivenName, shippingSurname] =
            BraintreeClient.transformAddressee(
                this.transactionInfo.deliveryAddress.addressee
            );

        if (!this.hostedFieldsInstance) {
            throw new Error('No Braintree hosted fields instance found');
        }

        const tokenizeResponse: HostedFieldsTokenizePayload =
            await this.hostedFieldsInstance.tokenize();

        // DefinitelyTyped aren't exactly up to date with the braintree
        // types hence the ts-ignores

        // @ts-ignore
        const verifyCardResponse: ThreeDSecureVerifyPayload =
            // @ts-ignore
            await this.threeDSecureInstance.verifyCard({
                amount: centToDecimal(this.transactionInfo.total).toString(),
                nonce: tokenizeResponse.nonce,
                bin: tokenizeResponse.details.bin,
                email: this.transactionInfo.customer.emailAddress,
                billingAddress: {
                    givenName: toAsciiPrintable(billingGivenName),
                    surname: toAsciiPrintable(billingSurname),
                    phoneNumber: this.transactionInfo.customer.phoneNumber,
                    ...BraintreeClient.transformAddress(
                        this.transactionInfo.billingAddress
                    ),
                },
                additionalInformation: {
                    shippingGivenName,
                    shippingSurname,
                    shippingPhone: this.transactionInfo.customer.phoneNumber,
                    shippingAddress: BraintreeClient.transformAddress(
                        this.transactionInfo.deliveryAddress
                    ),
                },

                addFrame: () => this.callbacks.onIframeAdd,
                removeFrame: () => this.callbacks.onIframeRemove,
                // @ts-ignore
                onLookupComplete: (data: any, next: () => void) => {
                    next();
                },
            });

        const failedStatusList = [
            Braintree3DSecureStatus.AUTHENTICATE_FRICTIONLESS_FAILED,
            Braintree3DSecureStatus.AUTHENTICATE_UNABLE_TO_AUTHENTICATE,
            Braintree3DSecureStatus.AUTHENTICATE_REJECTED,
            Braintree3DSecureStatus.AUTHENTICATE_UNAVAILABLE,
            Braintree3DSecureStatus.LOOKUP_ERROR,
            Braintree3DSecureStatus.LOOKUP_FAILED_ACS_ERROR,
            Braintree3DSecureStatus.LOOKUP_BYPASSED,
            Braintree3DSecureStatus.CHALLENGE_REQUIRED,
            Braintree3DSecureStatus.AUTHENTICATE_ERROR,
            Braintree3DSecureStatus.DATA_ONLY_SUCCESSFUL,
        ];

        const statusError: BraintreeError = {
            code: '2000',
            message: 'Do Not Honor',
            details: 'threeDSecure failed',
            type: 'MERCHANT',
            name: 'BraintreeError',
        };

        if (
            failedStatusList.includes(
                // @ts-ignore
                verifyCardResponse?.threeDSecureInfo?.status
            )
        ) {
            return Promise.reject(statusError);
        }

        return {
            nonce: verifyCardResponse.nonce,
            deviceData: this?.dataCollectorInstance?.deviceData,
            checkoutType: CheckoutType.CARD,
        };
    }

    /**
     * Get the transaction total
     */
    public get transactionTotal() {
        return this.transactionInfo.total;
    }

    /**
     * Sets the total on the transaction information when sending to braintree
     */
    public setTransactionTotal(total: number) {
        this.transactionInfo.total = total;
    }

    /**
     * Set the callback that gets called when paypal completes
     * @param onPayPalCompleteCallback The callback being run when paypal completes
     */
    public setOnPayPalComplete(onPayPalCompleteCallback: any) {
        this.callbacks.onPayPalComplete = onPayPalCompleteCallback;
    }

    /**
     * Transforms an addressee into two names. Taking the first and last part of
     * the name split into an array by whitespace
     * @param addressee
     */
    private static transformAddressee(addressee: string): string[] {
        const addresseeParts = addressee.split(' ');
        return [addresseeParts[0], addresseeParts[addresseeParts.length - 1]];
    }

    /**
     * Transforms an address into a Braintree compatible address used in
     * ThreeDSecure verification
     *
     * @param address
     */
    private static transformAddress(address: Address): any {
        return {
            streetAddress: address.line1,
            extendedAddress: address.line2,
            locality: address.town,
            // Remove region unless US as we cannot provide a 2-3 character code
            // as the user enters a free type value
            region: address.country === 'US' ? address.state : undefined,
            postalCode:
                address[address.country === 'US' ? 'zipcode' : 'postcode'],
            countryCodeAlpha2: address.country,
        };
    }

    /**
     * Handle creating a PayPal payment
     */
    private handlePaypalPayment(): void {
        try {
            return this.paypalCheckoutInstance.createPayment({
                flow: 'checkout',
                enableShippingAddress: true,
                shippingAddressEditable: false,
                intent: 'authorize',
                amount: centToDecimal(this.transactionInfo.total),
                currency: this.transactionInfo.currency,
            });
        } catch (error: any) {
            console.error({
                code: ErrorCodes.PAYPAL_CREATE_PAYMENT_FAILED,
                message: error,
            });
        }
    }

    /**
     * Handle a successful authorized PayPal payment
     *
     * @param data
     * @param actions
     */
    private async handlePaypalAuthorization(
        data: any,
        actions: any
    ): Promise<void> {
        try {
            this.callbacks.onCheckoutAttempted(CheckoutType.PAYPAL);
            const { nonce } = await this.paypalCheckoutInstance.tokenizePayment(
                data
            );

            this.nonce = nonce;
            return this.callbacks.onPayPalComplete(
                nonce,
                this?.dataCollectorInstance?.deviceData
            );
        } catch (error: any) {
            if (
                error === PayPalErrorTypes.INSTRUMENT_DECLINED ||
                error?.message === PayPalErrorTypes.INSTRUMENT_DECLINED
            ) {
                if (++this.paypalAttemptCount < this.paypalAttemptLimit) {
                    actions.restart();
                } else {
                    this.callbacks.onError(
                        {
                            code: ErrorCodes.PAYPAL_DECLINED,
                        },
                        CheckoutType.PAYPAL
                    );
                }
            } else {
                this.callbacks.onError(
                    {
                        code: ErrorCodes.PAYPAL_ERROR,
                        message: error,
                    },
                    CheckoutType.PAYPAL
                );
            }
        }
    }
}
